import { definedMap, hasOwnProperty } from "@glideapps/ts-necessities";
import { isLeft, isRight } from "fp-ts/lib/Either";
import * as iots from "io-ts";
import toPairs from "lodash/toPairs";
import * as pako from "pako";
import { v4 as uuid } from "uuid";
import type { AppPlanCode } from "../app-plans";
import { base64DecodeString, base64EncodeString } from "../base64";
import type { ThemeOverlay } from "../BaseTheme";
import type { BoostCode } from "../boosts";
import { removeComponentIDsFromAppDescription } from "../components/remove-component-id";
import type {
    AppDescription,
    AppFeatures,
    AppKind,
    AppLoginData,
    AppManifest,
    NonUserAppFeatures,
    SerializedApp,
    UserFeatures,
} from "../components/SerializedApp";
import {
    appDescriptionCodec,
    defaultUserFeatures,
    getAppFeatures,
    isolateAppDescription,
    isolatePersistentAppFeatures,
    isolateUserSettableAppFeatures,
} from "../components/SerializedApp";
import type { TableName } from "../database-strings";
import {
    ActionKind,
    allowedEmailsDocumentName,
    appAnalyticsCollectionName,
    appIntegrationsCollection,
    appsGrandfatheringCollectionName,
    appUserIDFieldName,
    builderAppsCollectionName,
    documentIDForTableName,
    geocodingCollectionName,
    glideAppsCollectionName,
    glideAppsDataCollectionName,
    glideAppsMetadataCollectionName,
    makeActionDocumentPath,
    makeTablesPath,
    makeUserProfileDocumentPathForApp,
    newFeaturesCollectionName,
    newFeaturesSeenCollectionName,
    ownerAppUserIDsFieldName,
    rowVersionFieldName,
    tableNameCodec,
    templateSubmissionsCollectionName,
    uniqueUsersFieldName,
    usersCollectionName,
    zapierDocName,
    zapsCollectionForAppID,
} from "../database-strings";
import { logError, logInfo } from "../debug-print";
import type { TableColumn, TypeSchema } from "../description";
import type {
    AirtableIntegration,
    BaseRowIndex,
    CompanyInformation,
    GoogleDriveIntegration,
    Integration,
    MicrosoftIntegration,
    ReasonForGlide,
    SelfReportedReferralSource,
    StripeIntegration,
    StripeTemplateStoreIntegration,
    UserFlags,
} from "../firebase-function-types";
import { baseRowIndexCodec, checkBaseRowIndex, isBaseRowIndex } from "../firebase-function-types";
import { rowIndexColumnName } from "../google-sheets";
import { getAllShortnameEndings } from "../location-common";
import type { SourceMetadata } from "../source-metadata";
import type { JSONData, JSONObject } from "../Support";
import {
    assert,
    checkArray,
    checkNumber,
    checkString,
    exceptionToString,
    isUndefinedish,
    mapFilterUndefined,
    panic,
    replaceArrayItem,
    updateDefined,
} from "../Support";
import type {
    AsDocumentData,
    Batch as BatchType,
    Database,
    DataReader as DataReaderType,
    DataReaderWriter as DataReaderWriterType,
    DataWriter as DataWriterType,
    DiffResults as DiffResultsType,
    DocumentData as DocumentDataType,
    DocumentDataWithID as DocumentDataWithIDType,
    DocumentDataWithIDAndPath as DocumentDataWithIDAndPathType,
    Query as QueryType,
    QueryableDataReader,
    QueryOpString as QueryOpStringType,
    QueryResults as QueryResultsType,
    SortDirection as SortDirectionType,
    Transaction as TransactionType,
} from "./core";
import type { Eminence } from "./eminence";
import { OwnerKind } from "./owner-kind";
import type { Pricing } from "./pricing";
import { pricingCodec } from "./pricing";
import type { StripeDate, StripeInvoicePaymentFailure } from "./stripe-info";

export { Database } from "./core";

export type DocumentData = DocumentDataType;
export type QueryOpString = QueryOpStringType;
export type Query = QueryType;
export type DocumentDataWithID = DocumentDataWithIDType;
export type DocumentDataWithIDAndPath = DocumentDataWithIDAndPathType;
export type QueryResults = QueryResultsType;
export type SortDirection = SortDirectionType;
export type DataReader = DataReaderType;
export type DataWriter = DataWriterType;
export type DataReaderWriter = DataReaderWriterType;
export type Batch = BatchType;
export type Transaction = TransactionType;
export type DiffResults = DiffResultsType;

export const documentIDColumnName = "$documentID";

export type DataSourceOrgs = "google" | "microsoft";

function isGoogleDriveIntegration(i: Integration): i is GoogleDriveIntegration {
    return i.kind === "google-drive";
}

function isStripeIntegration(i: Integration): i is StripeIntegration {
    return i.kind === "stripe";
}

function isStripeTemplateStoreIntegration(i: Integration): i is StripeTemplateStoreIntegration {
    return i.kind === "stripe-template-store";
}

function isMicrosoftIntegration(i: Integration): i is MicrosoftIntegration {
    return i.kind === "microsoft";
}

function isAirtableIntegration(i: Integration): i is AirtableIntegration {
    return i.kind === "airtable";
}

function sanitizeStripeIntegrationForUserSetting<T extends StripeIntegration | StripeTemplateStoreIntegration>(
    stripeIntegration: T
): T {
    const copy = { ...stripeIntegration };
    delete copy.waiveTransactionFees;
    return copy;
}

export function sanitizeIntegrationForUserSetting(integration: Integration): Integration {
    if (isStripeIntegration(integration) || isStripeTemplateStoreIntegration(integration)) {
        return sanitizeStripeIntegrationForUserSetting(integration);
    }
    return integration;
}

export function setOrUpdateIntegration(
    integrations: ReadonlyArray<Integration>,
    integration: Integration
): ReadonlyArray<Integration> {
    const index = integrations.findIndex(i => i.name === integration.name && i.kind === integration.kind);
    if (index >= 0) {
        return replaceArrayItem(integrations, index, integration);
    } else {
        return integrations.concat(integration);
    }
}

export function removeIntegration(
    integrations: ReadonlyArray<Integration>,
    integration: Integration
): ReadonlyArray<Integration> {
    const index = integrations.findIndex(i => i.name === integration.name && i.kind === integration.kind);
    if (index < 0) return integrations;
    return [...integrations.slice(0, index), ...integrations.slice(index + 1)];
}

export const defaultUserFlags: UserFlags = {
    hasSeenOrganizeTip: false,
    hasSeenNavigationTip: false,
    hasSeenHireAnExpertDialog: false,
    hasSeenHireAnExpertHelpMenuItem: false,
    onboardingComplete: false,
    tourComplete: false,
    filterWarningComplete: false,
    visibilityWarningComplete: false,
    unprotectedColumnWarningComplete: false,
    allowEmail: false,
    disabledNewComponentsForNewColumns: false,
    disabledLinkTablesModal: false,
    lastAcceptedTermsOfService: 0,
    builderTheme: "light",
    useSystemTheme: true,
    builderTrackingOptOut: false,
    hidePagesAdvert: false,
};

export enum StripeMessage {
    DowngradeDeleteFailed = "downgrade-delete-failed",
    DowngradeUpdateQuantityFailed = "downgrade-update-qauntity-failed",
    UpgradeCreateFailed = "upgrade-create-failed",
    UpgradeUpdateQuantityFailed = "upgrade-update-quantity-failed",
    CreateQueued = "create-queued",
    OK = "ok",
}

export interface AppPlanCodeEntry {
    readonly code: AppPlanCode;
    readonly stripeID: string;
    readonly appIDs: readonly string[];
    readonly userCount?: Readonly<Record<string, number>>;
    readonly stripeMessage: StripeMessage;
}

export interface OverageLineItem {
    readonly code: AppPlanCode;
    readonly stripeID: string;
    readonly appIDs: readonly string[];
    readonly stripeMessage: StripeMessage;
}

export enum SubscriptionKind {
    Monthly,
    Annual,
}

export interface SubscriptionDiscount {
    readonly promoCode: string;
    readonly reduction: number | { amount: number; currency: string };
    // typeof applies === "number" implies a UTC Unix Epoch timestamp of
    // expiration.
    readonly applies: "once" | "forever" | number;
}

interface SubscriptionBase {
    // A subscription ID can go undefined when the Boost subscription
    // is cancelled due to inactivity. The Boost settings are all still there,
    // waiting to be reinstalled on a now-gone subscription.
    readonly subscriptionID: string | undefined;
    readonly appPlanCodes?: readonly AppPlanCodeEntry[];
    readonly serial: number;
    // This subscription object doesn't include expiration,
    // payment validity, or any information about the payment method.
    // You'll have to query Stripe for all of that. However, we do
    // keep the state.
    readonly stripeState: "active" | "past_due" | "unpaid";
    readonly nextInvoice?: StripeDate;
    readonly nextTotal?: number;
    readonly currency?: string;
    readonly lastPaymentFailure?: StripeInvoicePaymentFailure;
    readonly appliedDiscount?: SubscriptionDiscount;
}

export type BoostCodeEntry = {
    readonly code: BoostCode;
    readonly stripeID: string;
};

export interface AnnualSubscription extends SubscriptionBase {
    readonly kind: SubscriptionKind.Annual;
}
export interface MonthlySubscription extends SubscriptionBase {
    readonly kind: SubscriptionKind.Monthly;
    readonly boostCodes: readonly BoostCodeEntry[];
    readonly overageItems?: readonly OverageLineItem[];
}

export interface OwnerSuspensions {
    // If payment is suspended for an Owner, Buy Buttons do not function.
    readonly payment?: boolean;
}

interface StripeCustomerInformation {
    // This serial is unfortunately shared with expectedSubscriptionSerial
    readonly serial: number;
    readonly customerName?: string;
    readonly additionalInfo?: string;
}

export type AppTrialType = "private-pro";
export interface AppTrialInfo {
    readonly trialType: AppTrialType;
    readonly templateSourceID: string;
    // This should not be undefined but is possible when converting an undefined value from firestore
    readonly trialStarted: number | undefined;
}

interface OwnerBase {
    readonly id: string;
    readonly displayName?: string;
    readonly appIDs: readonly string[];
    readonly integrations: readonly Integration[];
    readonly stripeCustomerIDs: readonly string[];
    // When we finally have annual subscriptions, they
    // will share the expected serial. After all, why not?
    readonly expectedSubscriptionSerial?: number;
    readonly monthlySubscription?: MonthlySubscription;
    readonly annualSubscription?: AnnualSubscription;
    // This was added in retrospect to support EU VAT,
    // and is not generally set.
    readonly customerInformation: StripeCustomerInformation | undefined;
    readonly suspensions?: OwnerSuspensions;
    readonly features?: UserFeatures;

    readonly templateLicenses: readonly string[];
    // Promo codes generally can only be used by new customers.
    // Sometimes, we want them to apply retroactively, so we'll
    // establish a "timeout period" on user accounts for them
    // to do so.
    readonly promoCodeExtensionUntil?: number;

    // appTrials are keyed off appID
    // The AppTrialInfo | undefined here is for type safety because not all apps are trials
    readonly appTrials: Readonly<Record<string, AppTrialInfo | undefined>> | undefined;
    // Get this with Date.now()
    readonly lastEmailVerificationDate?: number;
}

export interface ZapData {
    id: string;
    name: string;
}

export enum IntegrationKind {
    Webhook = "webhook",
}

export interface WebhookIntegration {
    readonly kind: IntegrationKind.Webhook;
    readonly name: string;
    readonly url: string;
    readonly password: string;
}

export interface WebhookIntegrationWithID extends WebhookIntegration {
    readonly id: string;
}

interface UserPlanCounts {
    readonly pro: number;
}

interface UserPlanAppIDs {
    readonly pro: readonly string[];
}

export interface TemplateSubmission {
    readonly sourceAppID: string;
    readonly status: "pending" | "approved" | "denied";
    readonly category: string;
    readonly description: string;
    readonly usageVideoURL: string | undefined;
    readonly price: number;
    readonly updatedAt: Date;
}

export interface TemplateSubmissions {
    readonly submissions: readonly TemplateSubmission[];
}

export interface UserData extends OwnerBase {
    // FIXME: Rename this to `User`.
    readonly kind: OwnerKind.User;

    readonly authID: string; // the authorized user's Firebase user ID
    readonly hubspotContactID?: string; // May not have a hubspot contact id if we still never created this contact
    readonly email?: string;
    readonly phoneNumber?: string;
    readonly photoURL?: string;

    readonly flags?: UserFlags;
    readonly orgUserIDs: readonly string[];
    readonly planCounts: UserPlanCounts;
    readonly planAppIDs: UserPlanAppIDs;
    readonly referrals: number;

    readonly allowMyApps?: boolean;
    // onboarding
    readonly reasonForGlide?: ReasonForGlide;
    readonly reasonForGlideOtherExplanation?: string;
    readonly selfReportedReferralSource?: SelfReportedReferralSource;
    readonly selfReportedReferralSourceOtherExplanation?: string;
    readonly partnerStackReferralPartnerKey?: string;
}

interface OrganizationPlanCounts {
    readonly organizationPublic: number;
}

interface OrganizationPlanAppIDs {
    readonly organizationPublic: readonly string[];
}

export interface Organization extends OwnerBase {
    readonly kind: OwnerKind.Organization;

    readonly companyInformation: CompanyInformation;
    readonly displayName: string;
    readonly memberUserIDs: readonly string[];
    readonly planCounts: OrganizationPlanCounts;
    readonly planAppIDs: OrganizationPlanAppIDs;
    readonly hasSubscription: boolean;
    readonly isAgency: boolean;
}

export type Owner = UserData | Organization;

export function makeGrandfatheredAppFeatures(
    appGrandfathering: Partial<GrandfatheringFeatures> | undefined
): Partial<AppFeatures> {
    const grandfatheredFeatures: Partial<AppFeatures> = {};
    if (appGrandfathering !== undefined) {
        for (const [k, v] of toPairs(appGrandfathering)) {
            // ##grandfatheringOnlyTrue
            if (v === true) {
                (grandfatheredFeatures as any)[k] = true;
            }
        }
    }
    return grandfatheredFeatures;
}

export function makeAppFeatures(
    userData: Owner | null | "loading" | undefined,
    appGrandfathering: AppGrandfathering | undefined,
    nonUserAppFeatures: NonUserAppFeatures,
    appKindOverride: AppKind | undefined
): AppFeatures {
    if (userData === undefined) {
        userData = null;
    }

    const grandfatheredFeatures = makeGrandfatheredAppFeatures(appGrandfathering?.features);

    let features: AppFeatures = {
        ...getUserFeatures(userData),
        ...isolateUserSettableAppFeatures(nonUserAppFeatures),
        ...isolatePersistentAppFeatures(nonUserAppFeatures),
        ...grandfatheredFeatures,
    };

    if (appKindOverride !== undefined) {
        features = { ...features, appKind: appKindOverride };
    }
    return features;
}

export type UserDataAndState = UserData | null | "loading";

export function isNullOrLoading<T>(x: T | null | "loading"): x is null | "loading" {
    return x === null || x === "loading";
}

export function isUserData(v: UserDataAndState): v is UserData {
    return !isNullOrLoading(v);
}

export function getUserFlags(user: UserDataAndState): UserFlags {
    if (!isUserData(user)) return defaultUserFlags;
    const { flags } = user;
    if (flags === undefined) return defaultUserFlags;
    return { ...defaultUserFlags, ...flags };
}

export function getUserFeatures(userData: Owner | null | "loading"): UserFeatures {
    if (userData === null || userData === "loading" || userData.features === undefined) {
        return defaultUserFeatures;
    }
    return updateDefined(defaultUserFeatures, userData.features);
}

export function getGoogleDriveOAuth2OfflineIntegration(user: UserData): GoogleDriveIntegration | undefined {
    return user.integrations.find(
        (i): i is GoogleDriveIntegration => isGoogleDriveIntegration(i) && i.authentication.kind === "oauth2-offline"
    );
}

export function getStripeIntegration(user: Owner): StripeIntegration | undefined {
    return user.integrations.find(isStripeIntegration);
}

export function getStripeTemplateStoreIntegration(user: Owner): StripeTemplateStoreIntegration | undefined {
    return user.integrations.find(isStripeTemplateStoreIntegration);
}

export function getMicrosoftIntegration(user: Owner, microsoftAccountID: string): MicrosoftIntegration | undefined {
    return user.integrations.filter(i => i.name === microsoftAccountID).find(isMicrosoftIntegration);
}

export function getMicrosoftIntegrations(user: Owner): MicrosoftIntegration[] {
    return user.integrations.filter(isMicrosoftIntegration);
}

export function getAirtableIntegration(user: Owner): AirtableIntegration | undefined {
    return user.integrations.find(isAirtableIntegration);
}

export function getAppTrialInfo(owner: Owner, appID: string): AppTrialInfo | undefined {
    if (owner.appTrials === undefined) return undefined;
    const trialInfo = owner.appTrials[appID];
    return trialInfo;
}

export const fourteenDayMax = 14;

/**
 * @param a first date (in Date.now() format)
 * @param b second date (in Date.now() format)
 * @returns amount of days between those
 */
function differenceInDays(a: number, b: number): number {
    return Math.floor(Math.abs(a - b) / (24 * 60 * 60 * 1000));
}

// NOTE: Returns undefined if the trial is not found or trialStarted does not exist on a trial
// Returns the number of days that have elapse between starting a trial and now:
// Returns undefined if no trial exists for that app
export function daysSinceTrialStart(owner: Owner | undefined, appID: string, now: number): number | undefined {
    if (owner === undefined) return undefined;
    const proTrial = getAppTrialInfo(owner, appID);
    if (proTrial === undefined || proTrial.trialStarted === undefined) return undefined;
    // NOTE: Now is likely to be a later date than trial started but since
    // new Date() passed from the frontend, the server timestamp on the owner doc
    // could possibly be in the future so we need to handle that case as well
    const daysFromTrialStartToNow = differenceInDays(now, proTrial.trialStarted);
    return daysFromTrialStartToNow;
}

// Returns undefined no trial exists or there is some issue with the trial start timestamp
// Returns number clamped number from 0 to max number of days in a trial
export function daysLeftInTrial(daysSinceTrialStarted: number | undefined, maxDaysInTrial: number): number | undefined {
    if (daysSinceTrialStarted === undefined) return undefined;

    return maxDaysInTrial - daysSinceTrialStarted >= 0 ? maxDaysInTrial - daysSinceTrialStarted : 0;
}

interface StripePaymentInformation {
    readonly processor: "stripe";
    readonly stripeUserID: string;
    // Note that we don't actually save these in PublishedApp,
    // but instead only use them in the builder.
    readonly livePublishableKey?: string;
    readonly testPublishableKey?: string;
}

export type PaymentInformation = StripePaymentInformation;

export interface PaymentInformationForBuyButtons {
    readonly [buyButtonID: string]: PaymentInformation;
}

export interface PublishedAppBase {
    readonly publishedAt: Date | undefined;
    readonly shortName: string | undefined;
    readonly customDomain: string | undefined;
}

export interface PublishedAppSnapshot extends PublishedAppBase {
    readonly app: SerializedApp;
    readonly paymentInformation: PaymentInformationForBuyButtons | undefined;
    readonly eminence: Eminence;
    readonly compiledJS: string | undefined;
}

// This is additional stuff we need in the `play` function.
export interface PlayData {
    // We try to make this compatible with ##appLoginData
    readonly manifest: AppManifest | undefined;
    readonly features: AppFeatures | undefined;

    readonly themeOverlay: ThemeOverlay | undefined;
    readonly tabIcons: readonly string[] | undefined;
}

export interface PublishedApp extends PublishedAppBase, PlayData {
    readonly id: string;
    readonly builderAppTimestamp: Date | undefined;
    readonly desiredDomain: string | undefined;
    readonly blocked?: boolean;

    // We need this to sign the native table snapshots
    readonly sourceMetadataArray: readonly SourceMetadata[] | undefined;

    readonly compiledJS: string | undefined;
}

export function publishedAppString(publishedApp: PublishedAppSnapshot): string {
    return JSON.stringify({ ...publishedApp, publishedAt: publishedApp.publishedAt?.getTime() });
}

export function zipPublishedAppString(publishedApp: PublishedAppSnapshot): string {
    return zipString(publishedAppString(publishedApp));
}

// Note that this doesn't actually perform type checking.
export function unzipPublishedAppString(serialized: string): PublishedAppSnapshot | undefined {
    try {
        const rehydrated = JSON.parse(unzipString(serialized));
        return publishedAppSnapshotForJSON(rehydrated);
    } catch (e: unknown) {
        logError(`Could not unzip published app string: ${e}`);
        return undefined;
    }
}

export type CompatibilityIssues = ReadonlyArray<string>;

export interface MissingTablesAndColumns {
    readonly tables: readonly string[];
    readonly columns: readonly { readonly table: string; readonly columns: readonly string[] }[];
}

export interface CompatibilityProblems {
    readonly compatibilityIssues?: CompatibilityIssues;
    readonly compatibilityMissing?: MissingTablesAndColumns;
}

export function isolateCompatibilityProblems(p: CompatibilityProblems): CompatibilityProblems | undefined {
    const { compatibilityIssues, compatibilityMissing } = p;
    if (compatibilityIssues === undefined && compatibilityMissing === undefined) return undefined;
    return { compatibilityIssues, compatibilityMissing };
}

export interface TableSize {
    readonly name: TableName;
    readonly numRows: number;
}

export interface DataSize {
    readonly totalRows: number;
    readonly tables: ReadonlyArray<TableSize>;
}

interface SchemaInPublishedAppData {
    // FIXME: Remove `undefined` once we have schemas for all published app datas
    readonly schema: TypeSchema | undefined;
    readonly lastSchemaUpdateAt: Date | undefined;
    readonly lastSchemaUpdateReason: string | undefined;
}

export interface PublishedAppData extends CompatibilityProblems, SchemaInPublishedAppData {
    readonly data: JSONData | undefined;
    readonly dataTimestamp: Date | undefined;
    readonly userID: string | undefined;
    readonly password: string | undefined;
    readonly spreadsheetName: string | undefined;
    readonly dataSize: DataSize | undefined;
    // FIXME: Remove this once we use new-style quotas everywhere
    readonly numRowsUsedInApp: number | undefined;
    readonly version: number;
    readonly tablesUnusedInApp: readonly string[];
    // FIXME: Remove this, we shouldn't have non-tables app datas anymore
    readonly hasTables: boolean | undefined;
}

export interface AppMetadata {
    // Only written
    title?: string;
    // Only written
    createdAtOrBefore?: Date;
    // Used to decide whether we need automatic refresh
    lastRefreshed?: Date;
    refreshStartedAt?: Date;
    // Note that this field doesn't matter at all if refreshStartedAt
    // is undefined. We don't even care in that case.
    refreshRequiresFollowUp?: boolean;
    // This is set for new-style Pro upgrades. We use this to
    // automatically cancel payments when downgrades happen.
    proStripeSubscriptionID?: string;
    automaticRefreshMinutes?: number;
    // Used for tracking provenence of apps
    copiedFromAppID?: string;
    originTemplateAppID?: string;
    // FIXME: We shouldn't use ##lastPublishedAt anymore, and instead use
    // `publishedAt` in the published app document.
    lastPublishedAt?: Date;
    // Doesn't seem to be used
    shouldLogDataChanges?: boolean;
}

export enum AuthenticationMethod {
    // `None` is legacy.  We're not "producing" it anymore.  It's now
    // `PublicEmailPin` with `optional` set.
    None = "none", // signing in is not required, but allowed
    Disabled = "disabled", // signing in is not allowed
    Password = "password",
    PublicEmailPin = "public-email-pin",
    WhitelistEmailPin = "whitelist-email-pin",
    UserProfileEmailPin = "user-profile-email-pin",
    OrgMembers = "org-members",
    JustMe = "just-me",
}

export function isAuthenticationMethodPublic(method: AuthenticationMethod): boolean {
    return (
        method === AuthenticationMethod.None ||
        method === AuthenticationMethod.Disabled ||
        method === AuthenticationMethod.PublicEmailPin
    );
}

export const appAnalyticsCodec = iots.type({
    // This last30DayUniqueDevices count is "on probation" for being deprecated.
    // It was derived from Google Analytics in the player, and we don't use
    // Google Analytics in the player. The only reason it's not being outright
    // removed is that it is being used as a filter for reload issue notification.
    last30DayUniqueDevices: iots.number,
    lastUniqueAppUsersInWindow: iots.number,
});

export type AppAnalytics = Readonly<iots.TypeOf<typeof appAnalyticsCodec>>;

/**
 * Compresses a payload with gzip
 *
 * Browsers unfortunately have the ability to corrupt the encoding
 * of the output; you should use {@link zipStringBase64} instead
 * to mitigate this on the frontend. This is always safe to do on
 * the backend; we don't expect any string encoding bugs there.
 *
 * Use {@link unzipString} to decompress the output of this function.
 *
 * @param s Payload to compress
 * @returns The compressed payload, as a string
 */
export function zipString(s: string): string {
    return pako.gzip(s, { to: "string" });
}

/**
 * Compresses a payload with gzip, base64 encoding the results.
 *
 * This is particularly useful on the frontend, where environmental
 * misconfiguration can result in irrecoverable string encoding
 * corruption.
 *
 * Use {@link unzipStringBase64} to decompress the output of this function.
 *
 * @param s Payload to compress
 * @returns The compressed payload, encoded in Base64
 */
export function zipStringBase64(s: string): string {
    return base64EncodeString(zipString(s));
}

/**
 * Decompresses the output of {@link zipString}
 *
 * @param s The compressed payload, as a bare binary string
 * @returns The decompressed payload corresponding to the input
 */
export function unzipString(s: string): string {
    try {
        return pako.ungzip(s, { to: "string" });
    } catch {
        return s;
    }
}

/**
 * Decompresses the output of {@link zipStringBase64}
 *
 * @param s The compressed payload, as a base64-encoded string
 * @returns The decompressed payload corresponding to the input
 */
export function unzipStringBase64(s: string): string {
    return pako.ungzip(base64DecodeString(s), { to: "string" });
}

function isAppDomainName(appNameOrID: string): boolean {
    return appNameOrID.includes(".");
}

export function isShortName(url: string) {
    return getAllShortnameEndings().some(sn => url.endsWith(sn));
}

export function appNameSearchField(appNameOrID: string): string | undefined {
    if (isAppDomainName(appNameOrID)) {
        return isShortName(appNameOrID) ? "shortName" : "customDomain";
    }
    return undefined;
}

export function isSizeLimitException(e: unknown): boolean {
    return hasOwnProperty(e, "code") && e.code === "invalid-argument" && e.toString().includes("is longer than");
}

export function getPlayDataFromSerializedApp(app: SerializedApp): PlayData {
    return {
        manifest: app.manifest,
        features: getAppFeatures(app),
        themeOverlay: app.theme.themeOverlay ?? "none",
        tabIcons: mapFilterUndefined(app.tabs ?? [], t => (t.hidden ? undefined : t.icon)),
    };
}

export function publishedAppForDocument(db: DataReader, data: DocumentData, id: string): PublishedApp {
    const compiledJS = definedMap(data.compiledJS, unzipString);

    return {
        compiledJS,
        id,
        publishedAt: definedMap(data.publishedAt, d => db.database.dateFromTimestamp(d)),
        builderAppTimestamp: definedMap(data.builderAppTimestamp, d => db.database.dateFromTimestamp(d)),
        shortName: data.shortName,
        customDomain: data.customDomain,
        desiredDomain: data.desiredDomain,
        blocked: data.blocked,
        manifest: data.manifest,
        features: data.features,
        themeOverlay: data.themeOverlay,
        tabIcons: data.tabIcons,
        sourceMetadataArray: data.sourceMetadataArray,
    };
}

export function publishedAppSnapshotForJSON(json: JSONObject): PublishedAppSnapshot {
    return {
        ...json,
        publishedAt: definedMap(json.publishedAt, d => new Date(d as number)) ?? new Date(),
    } as PublishedAppSnapshot;
}

export async function loadPublishedAppFromAppID(db: DataReader, appID: string): Promise<PublishedApp | undefined> {
    const data = await db.getDocument(glideAppsCollectionName, appID);
    if (data === undefined) return undefined;
    return publishedAppForDocument(db.database, data, appID);
}

export async function loadPublishedApp(db: QueryableDataReader, nameOrID: string): Promise<PublishedApp | undefined> {
    const field = appNameSearchField(nameOrID);
    if (field === undefined) {
        return await loadPublishedAppFromAppID(db, nameOrID);
    }

    // we have a custom domain or short name
    const result = await db.getDocumentsWhere(glideAppsCollectionName, [
        {
            fieldPath: field,
            opString: "==",
            value: nameOrID,
        },
    ]);

    if (result.length < 1) return undefined;

    const data = result[0].data;
    const resultId = result[0].id;

    return publishedAppForDocument(db, data, resultId);
}

function decodeSchema(schema: unknown): TypeSchema {
    if (typeof schema === "string") {
        return JSON.parse(unzipString(schema));
    } else {
        return schema as TypeSchema;
    }
}

export function checkTableColumn(c: TableColumn): void {
    assert(c.type !== undefined);
    assert(c.name !== undefined);
}

function encodeSchema(schema: TypeSchema): string {
    for (const t of schema.tables) {
        for (const c of t.columns) {
            checkTableColumn(c);
        }
    }
    return zipString(JSON.stringify(schema));
}

export function makeSchemaInPublishedAppData(
    db: Database,
    schema: TypeSchema,
    reason: string
): AsDocumentData<SchemaInPublishedAppData> {
    return {
        schema: encodeSchema(schema),
        lastSchemaUpdateAt: db.serverTimestampFieldValue,
        lastSchemaUpdateReason: reason,
    };
}

export function publishedAppDataForDocument(db: Database, data: DocumentData): PublishedAppData {
    const { data: compressedData, dataTimestamp, dataSize, schema } = data;
    return {
        data: definedMap(compressedData, d => JSON.parse(unzipString(d))),
        dataTimestamp: definedMap(dataTimestamp, db.dateFromTimestamp),
        password: data.password,
        spreadsheetName: data.spreadsheetName,
        userID: data.userID,
        schema: decodeSchema(schema),
        lastSchemaUpdateAt: definedMap(data.lastSchemaUpdateAt, db.dateFromTimestamp),
        lastSchemaUpdateReason: data.lastSchemaUpdateReason,
        compatibilityIssues: data.compatibilityIssues,
        compatibilityMissing: data.compatibilityMissing,
        dataSize: dataSize !== undefined && Array.isArray(dataSize.tables) ? dataSize : undefined,
        numRowsUsedInApp: data.numRowsUsedInApp,
        version: data.version ?? 0,
        tablesUnusedInApp: data.tablesUnusedInApp ?? [],
        hasTables: data.hasTables,
    };
}

export function publishedAppMetadataFromDocument(db: DataReaderWriter, data: DocumentData): AppMetadata {
    return db.database.convertFromDocument(data);
}

export async function loadAppMetadata(db: DataReaderWriter, id: string): Promise<AppMetadata | undefined> {
    const data = await db.getDocument(glideAppsMetadataCollectionName, id);
    return definedMap(data, d => publishedAppMetadataFromDocument(db, d));
}

export async function loadPublishedAppData(db: DataReader, id: string): Promise<PublishedAppData | undefined> {
    let data: DocumentData | undefined;
    try {
        data = await db.getDocument(glideAppsDataCollectionName, id);
        if (data === undefined) return undefined;
    } catch (e: unknown) {
        // This is what we get when the app data doesn't exist.
        if (hasOwnProperty(e, "code") && e.code === "permission-denied") return undefined;
        throw e;
    }
    return publishedAppDataForDocument(db.database, data);
}

export async function updateSchemaInPublishedAppData(
    db: DataWriter,
    appID: string,
    schema: TypeSchema,
    reason: string
): Promise<void> {
    const update = makeSchemaInPublishedAppData(db.database, schema, reason);
    await db.updateDocument(glideAppsDataCollectionName, appID, update);
}

function withRetry(run: (onError: (e: Error) => void) => () => void): () => void {
    let unsubscribe: (() => void) | undefined;
    let timeout: ReturnType<typeof setInterval> | undefined;
    let backOffTime = 10;
    const maxTimeout = 10000;

    function listen(): void {
        timeout = undefined;
        unsubscribe = run(_e => {
            if (unsubscribe !== undefined) {
                unsubscribe();
                unsubscribe = undefined;
            }

            // Back off by a factor of 10 (10ms, 100ms, 1s, 10s, 10s, 10s ...);
            timeout = setTimeout(listen, backOffTime);
            if (backOffTime < maxTimeout) {
                backOffTime *= 10;
            }
        });
    }

    listen();

    return () => {
        if (timeout !== undefined) {
            clearTimeout(timeout);
        }
        if (unsubscribe !== undefined) {
            unsubscribe();
        }
    };
}

export function listenWithRetry(
    db: Database,
    collectionName: string,
    documentID: string,
    onUpdate: (doc: DocumentData) => void
): () => void {
    return withRetry(onError =>
        db.listenToDocument(collectionName, documentID, onUpdate, e => {
            const logFunc = hasOwnProperty(e, "code") && e.code === "permission-denied" ? logInfo : logError;
            logFunc("Error listening to document", collectionName, documentID, e);
            onError(e);
        })
    );
}

export function listenWhereWithRetry(
    db: Database,
    collectionName: string,
    queries: readonly Query[],
    limit: number | undefined,
    onUpdate: (docs: readonly DocumentDataWithID[]) => Promise<void>
): () => void {
    return withRetry(onError =>
        db.listenWhere(collectionName, queries, undefined, undefined, limit, onUpdate, e => {
            logError("Error querying collection", collectionName, queries, e);
            onError(e);
        })
    );
}

export function listenToPublishedAppData(
    db: Database,
    id: string,
    onUpdate: (appData: PublishedAppData) => void
): () => void {
    return listenWithRetry(db, glideAppsDataCollectionName, id, data =>
        onUpdate(publishedAppDataForDocument(db, data))
    );
}

export function listenToPublishedAppMetadata(
    db: Database,
    id: string,
    onUpdate: (appData: AppMetadata) => void
): () => void {
    return listenWithRetry(db, glideAppsMetadataCollectionName, id, data =>
        onUpdate(publishedAppMetadataFromDocument(db, data))
    );
}

export interface AppLoginAuthenticationMethod {
    readonly authenticationMethod: AuthenticationMethod;
    readonly authenticationOptional: boolean; // valid only for auth methods that can be optional
}

export interface AppLoginAuthData extends AppLoginData, AppLoginAuthenticationMethod {
    readonly blacklistAppUserIDs: readonly string[];
    readonly passwordSerial?: number;
}

export interface AppLoginAuthDataFromPlay extends AppLoginAuthData {
    readonly appID: string;
    readonly timestamp: number;
}

export function appLoginFromDocumentData(data: DocumentData): AppLoginAuthData {
    return {
        ...data,
        authenticationMethod: data.authenticationMethod ?? AuthenticationMethod.None,
        authenticationOptional: data.authenticationOptional ?? false,
        blacklistAppUserIDs: data.blacklistAppUserIDs ?? [],
    } as unknown as AppLoginAuthData;
}

const builderAppDocument = iots.intersection([
    iots.union([iots.type({ appDescriptionString: iots.string }), iots.type({ appDescBase64: iots.string })]),
    iots.partial({
        templatePurchaseInformation: iots.type({
            sourceAppID: iots.string,
            sourceOwnerID: iots.string,
            pricing: pricingCodec,
        }),
        templateAuthorInformation: iots.type({
            name: iots.string,
            email: iots.string,
        }),
        // FIXME: type properly
        timestamp: iots.any,
        serial: iots.number,
        isPublishingActive: iots.boolean,
        restoreToBackupID: iots.string,
    }),
]);

const appDescriptionDocument = appDescriptionCodec;

export type TemplatePurchaseInformation = Readonly<{
    readonly sourceAppID: string;
    readonly sourceOwnerID: string;
    readonly pricing: Pricing;
}>;

export interface BuilderApp {
    readonly appDescription: AppDescription;
    // Note that these are only ever valid for templates.
    // We don't give template authors the ability to directly write
    // the template documents, so there is no risk of this being maliciously
    // overwritten by the author.
    readonly templatePurchaseInformation?: TemplatePurchaseInformation;
    readonly templateAuthorInformation?: {
        readonly name: string;
        readonly email: string;
    };
    readonly timestamp: Date | undefined;
    readonly serial: number | undefined;
    readonly isPublishingActive: boolean | undefined;
    readonly restoreToBackupID: string | undefined;
}

export function makeBuilderAppFromDocument(
    appID: string,
    db: Database,
    document: DocumentData
): BuilderApp | undefined {
    const decodedBuilderApp = builderAppDocument.decode(document);
    if (isLeft(decodedBuilderApp)) {
        logError("Error validating builder app document", appID);
        return undefined;
    }

    const builderApp = decodedBuilderApp.right;
    const {
        templatePurchaseInformation,
        templateAuthorInformation,
        timestamp,
        serial,
        isPublishingActive,
        restoreToBackupID,
    } = builderApp;
    let unzippedString: string;

    if (hasOwnProperty(builderApp, "appDescriptionString")) {
        unzippedString = unzipString(builderApp.appDescriptionString);
    } else {
        unzippedString = unzipStringBase64(builderApp.appDescBase64);
    }

    const parsed = JSON.parse(checkString(unzippedString));
    const decodedAppDescription = appDescriptionDocument.decode(parsed);

    if (isLeft(decodedAppDescription)) {
        logError("Error validating app description", appID);
        return undefined;
    }

    const appDescription = decodedAppDescription.right;

    delete (appDescription as any).input;

    return {
        appDescription,
        templatePurchaseInformation,
        templateAuthorInformation,
        timestamp: definedMap(timestamp, ts => db.dateFromTimestamp(ts)),
        serial,
        isPublishingActive,
        restoreToBackupID,
    };
}

const sessionID = uuid();

export function builderAppDocumentFromParts(
    db: Database,
    appDescription: AppDescription,
    useBase64: boolean,
    userID: string | undefined,
    prevSerial: number,
    forUpdate: boolean = true
): DocumentData {
    const descJSON = JSON.stringify(removeComponentIDsFromAppDescription(isolateAppDescription(appDescription)));
    const data: DocumentData = {
        timestamp: db.serverTimestampFieldValue,
        serial: prevSerial + 1,
        sessionID,
    };
    if (userID !== undefined) {
        data.savingUserID = userID;
    }
    if (useBase64) {
        data.appDescBase64 = zipStringBase64(descJSON);
        if (forUpdate) {
            data.appDescriptionString = db.deleteFieldValue;
        }
    } else {
        data.appDescriptionString = zipString(descJSON);
        if (forUpdate) {
            data.appDescBase64 = db.deleteFieldValue;
        }
    }
    return data;
}

export async function loadBuilderApp(rw: DataReader, id: string): Promise<BuilderApp | undefined> {
    const document = await rw.getDocument(builderAppsCollectionName, id);
    if (document === undefined) return undefined;
    try {
        return makeBuilderAppFromDocument(id, rw.database, document);
    } catch (e: unknown) {
        logError("Could not make builder app from document:", exceptionToString(e));
        return undefined;
    }
}

export interface AppWithSchema {
    readonly builderAppDocument: DocumentData;
    readonly builderApp: BuilderApp;
    readonly publishedAppData: PublishedAppData | undefined;
    readonly schema: TypeSchema;
    readonly userID: string | undefined;
}

export async function loadAppWithSchema(rw: DataReaderWriter, id: string): Promise<AppWithSchema | undefined> {
    const builderAppDoc = await rw.getDocument(builderAppsCollectionName, id);
    if (builderAppDoc === undefined) {
        logError("Could not load builder app", id);
        return undefined;
    }
    const builderApp = makeBuilderAppFromDocument(id, rw.database, builderAppDoc);
    if (builderApp === undefined) return undefined;

    const publishedAppData = await loadPublishedAppData(rw, id);
    const schema = publishedAppData?.schema;
    if (schema === undefined) {
        logError("No schema in published app", id);
        return undefined;
    }

    return {
        builderApp,
        builderAppDocument: builderAppDoc,
        publishedAppData,
        schema,
        userID: definedMap(publishedAppData, p => p.userID),
    };
}

export const defaultUserPlanCounts: UserPlanCounts = {
    pro: 0,
};

export const defaultUserPlanAppIDs: UserPlanAppIDs = {
    pro: [],
};

export const defaultOrganizationPlanCounts: OrganizationPlanCounts = {
    organizationPublic: 0,
};

export const defaultOrganizationPlanAppIDs: OrganizationPlanAppIDs = {
    organizationPublic: [],
};

const minPlanCountForAdvancedPro = 50;

export function hasAdvancedPro(userData: UserData): boolean {
    const userFeatures = getUserFeatures(userData);
    return userFeatures.advancedPro || userData.planCounts.pro >= minPlanCountForAdvancedPro;
}

export function coerceAppTrialDates(
    db: Database,
    appTrials: any
): { trials: Record<string, AppTrialInfo | undefined>; brokenDates: boolean } | undefined {
    if (isUndefinedish(appTrials)) return undefined;
    const appTrialsWithDates: Record<string, AppTrialInfo> = {};
    let brokenDates = false;
    for (const [appTrialID, appTrialInfo] of toPairs<AppTrialInfo>(appTrials)) {
        if (appTrialInfo.trialStarted === undefined || typeof appTrialInfo.trialStarted === "number") {
            appTrialsWithDates[appTrialID] = { ...appTrialInfo };
            continue;
        }
        const validDate = db.dateFromTimestamp(appTrialInfo.trialStarted);
        brokenDates = true;
        appTrialsWithDates[appTrialID] = {
            ...appTrialInfo,
            trialStarted: validDate.getTime(),
        };
    }
    return { trials: appTrialsWithDates, brokenDates };
}

export function makeOwnerFromDocument(db: Database, data: DocumentData, id: string): Owner {
    const maybeAppTrialDates = coerceAppTrialDates(db, data.appTrials);
    let coercedAppTrials: Record<string, AppTrialInfo | undefined> | undefined;
    if (maybeAppTrialDates !== undefined) {
        coercedAppTrials = maybeAppTrialDates.trials;
    }

    const base = {
        id,
        displayName: data.displayName,
        appIDs: data.appIDs ?? [],
        integrations: data.integrations ?? [],
        stripeCustomerIDs: data.stripeCustomerIDs ?? [],
        hubspotContactID: data.hubspotContactID,
        monthlySubscription: { ...data.monthlySubscription, kind: SubscriptionKind.Monthly },
        annualSubscription: { ...data.annualSubscription, kind: SubscriptionKind.Annual },
        expectedSubscriptionSerial: data.expectedSubscriptionSerial,
        features: data.features,
        suspensions: data.suspensions,
        appTrials: coercedAppTrials ?? {},
        lastEmailVerificationDate: data.lastEmailVerificationDate,
    };

    if (data.kind === OwnerKind.Organization) {
        let result: Owner = {
            kind: OwnerKind.Organization,
            ...base,
            memberUserIDs: data.memberUserIDs ?? [],
            companyInformation: data.companyInformation,
            planCounts: data.planCounts ?? defaultOrganizationPlanCounts,
            planAppIDs: data.planAppIDs ?? defaultOrganizationPlanAppIDs,
            hasSubscription: data.hasSubscription ?? false,
            isAgency: data.isAgency ?? false,
            templateLicenses: data.templateLicenses ?? [],
            customerInformation: data.customerInformation ?? undefined,
        };

        if (result.planAppIDs.organizationPublic === undefined) {
            result = { ...result, planAppIDs: { ...result.planAppIDs, organizationPublic: [] } };
        }
        return result;
    } else {
        let result: Owner = {
            kind: OwnerKind.User,
            ...base,
            email: data.email,
            phoneNumber: data.phoneNumber,
            photoURL: data.photoURL,
            authID: data.authID,
            flags: data.flags,
            orgUserIDs: data.orgUserIDs ?? [],
            planCounts: data.planCounts ?? defaultUserPlanCounts,
            planAppIDs: data.planAppIDs ?? defaultUserPlanAppIDs,
            templateLicenses: data.templateLicenses ?? [],
            referrals: data.referrals ?? 0,
            reasonForGlide: data.reasonForGlide,
            selfReportedReferralSource: data.selfReportedReferralSource,
            selfReportedReferralSourceOtherExplanation: data.selfReportedReferralSourceOtherExplanation,
            reasonForGlideOtherExplanation: data.reasonForGlideOtherExplanation,
            partnerStackReferralPartnerKey: data.partnerStackReferralPartnerKey,
            customerInformation: data.customerInformation ?? undefined,
            allowMyApps: data.allowMyApps,
        };

        if (result.planAppIDs.pro === undefined) {
            result = { ...result, planAppIDs: { ...result.planAppIDs, pro: [] } };
        }
        return result;
    }
}

export async function loadOwnersByQuery(db: Database, query: Query): Promise<Owner[]> {
    const results = await db.getDocumentsWhere(usersCollectionName, [query]);
    return results.map(({ data, id }) => makeOwnerFromDocument(db, data, id));
}

export async function loadOwnerByQuery(db: Database, query: Query): Promise<Owner | undefined> {
    const results = await loadOwnersByQuery(db, query);
    return results[0];
}

export async function loadUserByFirebaseID(db: Database, firebaseID: string): Promise<UserData | undefined> {
    const result = await loadOwnerByQuery(db, {
        fieldPath: "authID",
        opString: "==",
        value: firebaseID,
    });
    if (result === undefined || result.kind !== OwnerKind.User) return undefined;
    return result;
}

export async function loadOwner(db: DataReader, userID: string): Promise<Owner | undefined> {
    const doc = await db.getDocument(usersCollectionName, userID);
    return definedMap(doc, d => makeOwnerFromDocument(db.database, d, userID));
}

export function listenToUser(db: Database, userID: string, onUpdate: (userData: UserData) => void): () => void {
    return db.listenToDocument(usersCollectionName, userID, data => {
        const owner = makeOwnerFromDocument(db, data, userID);
        if (owner.kind !== OwnerKind.User) {
            return panic("Owner is not a user");
        }
        onUpdate(owner);
        return;
    });
}

export function listenToTemplateSubmissions(
    db: Database,
    appID: string,
    onUpdate: (templateSubmissions: readonly TemplateSubmission[]) => void
): () => void {
    return listenWhereWithRetry(
        db,
        templateSubmissionsCollectionName,
        [
            {
                fieldPath: "sourceAppID",
                opString: "==",
                value: appID,
            },
        ],
        undefined,
        async docs => {
            const templateSubmissions = docs.map(d => ({
                sourceAppID: d.data.sourceAppID,
                status: d.data.status,
                category: d.data.category,
                description: d.data.description,
                usageVideoURL: d.data.usageVideoURL,
                updatedAt: db.dateFromTimestamp(d.data.updatedAt),
                price: d.data.price ?? 0,
            }));
            onUpdate(templateSubmissions);
            return;
        }
    );
}

export function makeAppAnalyticsFromDocument(doc: DocumentData): AppAnalytics {
    return {
        last30DayUniqueDevices: doc.last30DayUniqueDevices ?? 0,
        lastUniqueAppUsersInWindow: doc[uniqueUsersFieldName] ?? 0,
    };
}

export async function loadAppAnalytics(db: DataReader, appID: string): Promise<AppAnalytics | undefined> {
    try {
        const doc = await db.getDocument(appAnalyticsCollectionName, appID);
        if (doc === undefined) return undefined;
        return makeAppAnalyticsFromDocument(doc);
    } catch {
        return undefined;
    }
}

export async function loadAppZapierAPIKey(db: Database, appID: string): Promise<string | undefined> {
    try {
        const doc = await db.getDocument(appIntegrationsCollection(appID), zapierDocName);
        if (doc === undefined) return undefined;
        return definedMap(doc.zapierAPIKey, checkString);
    } catch {
        return undefined;
    }
}

export function listenToAppZaps(db: Database, appID: string, onUpdate: (zaps: readonly ZapData[]) => void): () => void {
    return listenWhereWithRetry(
        db,
        zapsCollectionForAppID(appID),
        [
            {
                fieldPath: "isEnabled",
                opString: "==",
                value: true,
            },
        ],
        20,
        async docs => {
            const zaps = docs.map(d => ({ id: d.data.id, name: d.data.name }));
            onUpdate(zaps);
        }
    );
}

export function makeWebhookIntegrationFromDocument(
    doc: DocumentData,
    id: string
): WebhookIntegrationWithID | undefined {
    if (doc.kind !== IntegrationKind.Webhook) return undefined;
    return { ...doc, id } as unknown as WebhookIntegrationWithID;
}

export async function loadWebhookIntegration(
    db: Database,
    appID: string,
    webhookID: string
): Promise<WebhookIntegrationWithID | undefined> {
    const doc = await db.getDocument(appIntegrationsCollection(appID), webhookID);
    if (doc === undefined) return undefined;
    return makeWebhookIntegrationFromDocument(doc, webhookID);
}

export async function saveWebhookIntegration(
    db: DataWriter,
    appID: string,
    webhookID: string | undefined,
    wh: WebhookIntegration
): Promise<string> {
    const doc = db.database.convertToDocument(wh);
    return await db.setDocument(appIntegrationsCollection(appID), webhookID, doc);
}

export const webhookIntegrationQuery: Query = {
    fieldPath: "kind",
    opString: "==",
    value: IntegrationKind.Webhook,
};

export function listenToAppWebhooks(
    db: Database,
    appID: string,
    onUpdate: (webhooks: readonly WebhookIntegrationWithID[]) => void
): () => void {
    return listenWhereWithRetry(
        db,
        appIntegrationsCollection(appID),
        [webhookIntegrationQuery],
        undefined,
        async docs => {
            const integrations = mapFilterUndefined(docs, d => makeWebhookIntegrationFromDocument(d.data, d.id));
            onUpdate(integrations);
        }
    );
}

const userSpecificRowIndexCodec = iots.type({
    keyColumnName: iots.string,
    // FIXME: Be more precise with this
    keyColumnValue: iots.unknown,
});

export type UserSpecificRowIndex = iots.TypeOf<typeof userSpecificRowIndexCodec>;

const rowIndexCodec = iots.union([
    baseRowIndexCodec,
    iots.intersection([
        userSpecificRowIndexCodec,
        iots.partial({
            rowIndexHint: baseRowIndexCodec,
        }),
    ]),
]);
export type RowIndex = iots.TypeOf<typeof rowIndexCodec>;

export function isRowIndex(x: any): x is RowIndex {
    if (x === undefined) return false;
    return isRight(rowIndexCodec.decode(x));
}

export function areRowIndexesConflicting(a: RowIndex | undefined, b: RowIndex | undefined): boolean {
    if (a === undefined || b === undefined) return false;

    if (isBaseRowIndex(a)) {
        if (isBaseRowIndex(b)) {
            return a === b;
        } else {
            return a === b.rowIndexHint;
        }
    } else {
        if (isBaseRowIndex(b)) {
            return a.rowIndexHint === b;
        } else {
            return a.keyColumnName === b.keyColumnName && a.keyColumnValue === b.keyColumnValue;
        }
    }
}

export function getUserAgnosticRowIndex(x: RowIndex): BaseRowIndex | undefined {
    if (typeof x === "number" || typeof x === "string") return x;
    return x.rowIndexHint;
}

export function getUserSpecificRowIndex(x: RowIndex): UserSpecificRowIndex | undefined {
    if (typeof x !== "object") return undefined;
    return x;
}

export function decomposeRowIndex(ri: RowIndex): UserSpecificRowIndex {
    if (isBaseRowIndex(ri)) {
        return { keyColumnName: rowIndexColumnName, keyColumnValue: ri };
    } else {
        return ri;
    }
}

export const userSpecificRowCodec = iots.intersection([
    userSpecificRowIndexCodec,
    iots.type({
        appUserID: iots.string,
        data: iots.record(iots.string, iots.any),
    }),
    iots.partial({
        [rowVersionFieldName]: iots.number,
    }),
]);

export type UserSpecificRow = iots.TypeOf<typeof userSpecificRowCodec>;

// FIXME: be more precise with the values
const columnValues = iots.UnknownRecord;
export type ColumnValues = iots.TypeOf<typeof columnValues>;

export const actionDocumentCodec = iots.partial({
    authID: iots.string,
    appUserID: iots.string,
    deviceID: iots.string,
    fromBuilder: iots.boolean,
    fromDataEditor: iots.boolean,
});
type ActionDocument = iots.TypeOf<typeof actionDocumentCodec>;

async function postActionDocument(
    db: DataWriter,
    authID: string | undefined,
    appUserID: string | undefined,
    deviceID: string | undefined,
    path: string,
    actionDoc: ActionDocument
): Promise<string> {
    const doc: DocumentData = { ...actionDoc, writtenAt: db.database.serverTimestampFieldValue, receivedAt: false };
    if (authID !== undefined) {
        doc.authID = authID;
    }
    if (appUserID !== undefined) {
        doc.appUserID = appUserID;
    }
    if (deviceID !== undefined) {
        doc.deviceID = deviceID;
    }
    return await db.setDocument(path, undefined, doc);
}

// For some reason we still get action documents that have
// string table names.
const tableNameOrString = iots.union([tableNameCodec, iots.string]);

export function tryDecodeTableName(maybeTableName: unknown): TableName | string | undefined {
    const decodeResult = tableNameOrString.decode(maybeTableName);
    return isLeft(decodeResult) ? undefined : decodeResult.right;
}

export const addRowToTableDocument = iots.intersection([
    actionDocumentCodec,
    iots.type({
        tableName: tableNameOrString,
        columnValues,
    }),
]);
export type AddRowToTableDocument = iots.TypeOf<typeof addRowToTableDocument>;

// NOTE: This is only used in the dummy storage controller.  Do not use for
// production purposes!
export async function postAddRowToTableAction(
    db: Database,
    appID: string,
    authID: string | undefined,
    deviceID: string | undefined,
    tableName: TableName,
    values: ColumnValues
): Promise<string> {
    const doc: AddRowToTableDocument = { tableName, columnValues: values, fromDataEditor: false, fromBuilder: false };
    return await postActionDocument(
        db,
        authID,
        undefined,
        deviceID,
        makeActionDocumentPath(appID, ActionKind.AddRowToTable),
        doc
    );
}

export const setColumnsInRowDocument = iots.intersection([
    actionDocumentCodec,
    iots.type({
        tableName: tableNameOrString,
        rowIndex: rowIndexCodec,
        columnValues,
    }),
]);
export type SetColumnsInRowDocument = iots.TypeOf<typeof setColumnsInRowDocument>;

export async function postSetColumnsInRowAction(
    db: Database,
    appID: string,
    authID: string | undefined,
    appUserID: string | undefined,
    deviceID: string | undefined,
    tableName: TableName,
    rowIndex: RowIndex,
    values: ColumnValues,
    fromBuilder: boolean,
    fromDataEditor: boolean
): Promise<string> {
    const doc: SetColumnsInRowDocument = {
        tableName,
        rowIndex,
        columnValues: values,
        fromBuilder,
        fromDataEditor,
    };
    return await postActionDocument(
        db,
        authID,
        appUserID,
        deviceID,
        makeActionDocumentPath(appID, ActionKind.SetColumnsInRow),
        doc
    );
}

export const deleteRowDocument = iots.intersection([
    actionDocumentCodec,
    iots.type({
        tableName: tableNameOrString,
        rowIndex: iots.readonlyArray(rowIndexCodec),
    }),
]);
export type DeleteRowDocument = iots.TypeOf<typeof deleteRowDocument>;

export async function postDeleteRowAction(
    db: DataWriter,
    appID: string,
    authID: string | undefined,
    appUserID: string | undefined,
    deviceID: string | undefined,
    tableName: TableName,
    rowIndex: RowIndex,
    fromBuilder: boolean,
    fromDataEditor: boolean
): Promise<string> {
    const doc: DeleteRowDocument = { tableName, rowIndex: [rowIndex], fromBuilder, fromDataEditor };
    return await postActionDocument(
        db,
        authID,
        appUserID,
        deviceID,
        makeActionDocumentPath(appID, ActionKind.DeleteRow),
        doc
    );
}

export const deleteColumnDocument = iots.intersection([
    actionDocumentCodec,
    iots.type({
        tableName: tableNameCodec,
        columnName: iots.string,
    }),
]);
export type DeleteColumnDocument = iots.TypeOf<typeof deleteColumnDocument>;

// Returns the full action path
export async function postDeleteColumnAction(
    db: Database,
    appID: string,
    tableName: TableName,
    columnName: string
): Promise<string> {
    // This is only called from the builder.
    const doc: DeleteColumnDocument = { tableName, columnName, fromBuilder: true, fromDataEditor: true };
    const collection = makeActionDocumentPath(appID, ActionKind.DeleteColumn);
    const actionID = await postActionDocument(db, undefined, undefined, undefined, collection, doc);
    return `${collection}/${actionID}`;
}

export enum NewFeatureTag {
    New = "new",
    Update = "update",
}

export interface NewFeature {
    readonly title: string;
    readonly body: string;
    readonly learnMoreURL?: string;
    readonly tag: NewFeatureTag;
}

export async function loadNewFeatures(db: Database, userID: string): Promise<"never seen" | ReadonlyArray<NewFeature>> {
    const newestSeen = await db.getDocument(newFeaturesSeenCollectionName, userID);
    const queries: Query[] = [];
    queries.push({
        fieldPath: "date",
        opString: "<=",
        value: new Date(),
    });
    if (newestSeen === undefined) {
        return "never seen";
    } else {
        queries.push({
            fieldPath: "date",
            opString: ">=",
            value: newestSeen.lastSeen,
        });
    }

    const results = await db.getDocumentsWhere(newFeaturesCollectionName, queries);
    return results.map(({ data }) => data as NewFeature);
}

export async function saveNewestFeatureSeen(db: Database, userID: string): Promise<void> {
    await db.setDocument(newFeaturesSeenCollectionName, userID, { lastSeen: new Date() });
}

export interface RowDocumentDatas {
    readonly publicData?: DocumentData;
    readonly privateData?: DocumentData;
}

export function combineRowDocumentDatas({ publicData, privateData }: RowDocumentDatas): DocumentData {
    return { ...publicData, ...privateData };
}

export function setRowDocumentData(
    entry: RowDocumentDatas | undefined,
    data: DocumentData,
    isPrivate: boolean
): RowDocumentDatas {
    if (isPrivate) {
        entry = { ...entry, privateData: data };
    } else {
        entry = { ...entry, publicData: data };
    }
    return entry;
}

export function deleteRowDocumentData(entry: RowDocumentDatas, isPrivate: boolean): RowDocumentDatas | undefined {
    if (isPrivate) {
        entry = { ...entry, privateData: undefined };
    } else {
        entry = { ...entry, publicData: undefined };
    }
    if (entry.privateData === undefined && entry.publicData === undefined) {
        return undefined;
    }
    return entry;
}

export interface LatLng {
    readonly lat: number;
    readonly lng: number;
}

export async function loadLocationForAddress(db: Database, address: string): Promise<LatLng | undefined> {
    address = address.trim();

    const docs = await db.getDocumentsWhere(
        geocodingCollectionName,
        [{ fieldPath: "address", opString: "==", value: address }],
        undefined,
        undefined,
        1
    );
    if (docs.length !== 1) return undefined;

    const { lat, lng } = docs[0].data.location;
    return {
        lat: checkNumber(lat),
        lng: checkNumber(lng),
    };
}

export function makeAppUserIDQuery(appUserID: string): Query {
    return { fieldPath: appUserIDFieldName, opString: "==", value: appUserID };
}

export function makePrivateRowsQuery(appUserID: string, appUserRoles: readonly string[]): Query {
    return {
        fieldPath: ownerAppUserIDsFieldName,
        opString: "array-contains-any",
        value: [appUserID, ...appUserRoles],
    };
}

export function makeRowVersionQuery(version: number): Query {
    return { fieldPath: rowVersionFieldName, opString: ">", value: version };
}

export enum SnapshotKind {
    // Publicly accessible
    Public = "public",
    // Accessible only to whoever can sign in to the app
    Protected = "private",
    // Only accessible to the backend, contains only tables with row owners
    Backend = "backend",
    // Only accessible to the backend, contains tables that are unused in the app
    BackendUnused = "backend-unused",
    // Snapshots for individual native tables
    NativeTable = "native-table",
}

export function snapshotDirectory(kind: SnapshotKind): string {
    return `snapshots-${kind}`;
}

export function snapshotFilename(appID: string, kind: SnapshotKind): string {
    return `${snapshotDirectory(kind)}/${appID}.jzon`;
}

export interface TableDocuments {
    // Indexed by table name
    [name: string]: readonly DocumentDataWithID[];
}

export interface DataSnapshot {
    readonly data: TableDocuments;
    readonly version: number;
}

export interface TableData {
    readonly deletedRowIndexes: readonly BaseRowIndex[];
    readonly deletedRowIndexVersions: Record<string, number>;
    readonly version: number | undefined;
}

const deletedRowIndexVersionsCodec = iots.record(baseRowIndexCodec, iots.number);

export function makeTableDataFromDocument(data: DocumentData): TableData {
    let { deletedRowIndexes } = data;
    if (typeof deletedRowIndexes === "string") {
        deletedRowIndexes = JSON.parse(deletedRowIndexes);
    } else if (!Array.isArray(deletedRowIndexes)) {
        deletedRowIndexes = [];
    }
    checkArray(deletedRowIndexes, checkBaseRowIndex);
    let deletedRowIndexVersions: Record<string, number> = {};
    const { deletedRowIndexVersions: docDeletedRowIndexVersions } = data;

    if (docDeletedRowIndexVersions !== undefined) {
        try {
            const decoded = deletedRowIndexVersionsCodec.decode(JSON.parse(docDeletedRowIndexVersions));
            if (isRight(decoded)) {
                deletedRowIndexVersions = decoded.right;
            }
        } catch {
            // Oh no! The JSON didn't parse.
            // There's nothing intelligent we can do here.
        }
    }

    return { deletedRowIndexes, version: definedMap(data.version, checkNumber), deletedRowIndexVersions };
}

export async function loadTableData(
    db: DataReader,
    appID: string,
    tableName: TableName
): Promise<TableData | undefined> {
    const tablesPath = makeTablesPath(appID);
    return definedMap(await db.getDocument(tablesPath, documentIDForTableName(tableName)), makeTableDataFromDocument);
}

// ##grandfatheringOnlyTrue:
// We only apply grandfathering for features that are `true`.
export interface GrandfatheringFeatures extends AppFeatures {
    readonly unlimitedPasswordUsers: boolean;
    readonly allowRealEmails: boolean;
    readonly noRowLimit: boolean;
    readonly noGeocodeLimit: boolean;
    readonly canHaveEmailWhitelist: boolean;
}

export interface AppGrandfathering {
    readonly features: Partial<GrandfatheringFeatures>;
}

export const defaultAppGrandfathering: AppGrandfathering = {
    features: {},
};

export async function loadAppGrandfathering(
    db: DataReaderWriter,
    appID: string
): Promise<AppGrandfathering | undefined> {
    const doc = await db.getDocument(appsGrandfatheringCollectionName, appID);
    if (doc === undefined) return undefined;
    return doc as AppGrandfathering;
}

export interface AppUserForApp {
    readonly roles: readonly string[];
}

export function makeAppUserForAppFromDocument(doc: DocumentData): AppUserForApp {
    return { roles: checkArray(doc.roles, checkString) };
}

export async function loadAllowedEmails(tx: DataReader, appID: string): Promise<readonly string[] | undefined> {
    const userProfilePath = makeUserProfileDocumentPathForApp(appID);
    const doc = await tx.getDocument(userProfilePath, allowedEmailsDocumentName);
    if (doc === undefined) return undefined;
    return checkArray(JSON.parse(unzipString(doc.allowedEmails)), checkString);
}
